import { useCallback, useEffect, useLayoutEffect, useRef, useState, useSyncExternalStore } from "react";
import { supabase } from "@/integrations/supabase/client";
import useVoice from "@/hooks/useVoice";
import { appAudioFocus, type CoachingAudioLease } from "@/lib/appAudioFocus";
import { createMirrorAuthSessionScope, type MirrorAuthLease } from "@/lib/mirrorAuthSession";
import type { Lang } from "@/i18n/locale";

// The same silent unlock payload already used by MirrorChat. Never user audio.
const SILENT_MP3 = "data:audio/mpeg;base64,SUQzBAAAAAABEVRYWFgAAAAtAAADY29tbWVudABCaWdTb3VuZEJhbmsuY29tIC8gTGFTb25vdGhlcXVlLmNvbQBURU5DAAAABwAAA0xhbWUAAAAAAAAAAAAAAP/7kGQAAANUMEoFPeACNQV40KEAAA0gwX4UPYACNgV5Io5eBAgAADXCHP/7kmRAAADSBHIJaxiADNgFhw2EABN0Fch2dYAI2gWHDYSAAAANPJy//+5JkwAAA0wRyHZ2AAjYBYcNhAAAA0gRyHZ1gAjaBYcNhIAAAAf/+5JkCgAA0gRyHZ1gAjaBYcNhAAAA0gRyHZ1gAjYBYcNhIAAAPf/7kmQAAADSBHIdnWACNoFhw2EgAADSBHIdnWACNgFhw2EgAAA=";
type SpeechMessage = "audioError" | "audioQuota" | "signInToListen" | null;
interface SpeechRun { key: string; auth: MirrorAuthLease; focus: CoachingAudioLease }
interface Options { text: string; identity: string; language: Lang; paused: boolean }

export function useExerciseCoachSpeech(options: Options) {
  const latest = useRef(options);
  latest.current = options;
  const scope = useRef(createMirrorAuthSessionScope());
  const mounted = useRef(false);
  const runRef = useRef<SpeechRun | null>(null);
  const [audio, setAudio] = useState<HTMLAudioElement | null>(null);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const attachAudio = useCallback((element: HTMLAudioElement | null) => {
    audioRef.current = element;
    setAudio(element);
  }, []);
  const voice = useVoice({ externalAudioElement: audio });
  const voiceRef = useRef(voice);
  voiceRef.current = voice;
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<SpeechMessage>(null);
  const blocker = useSyncExternalStore(appAudioFocus.subscribe, appAudioFocus.blocking, () => null);

  const cancel = useCallback(() => {
    const run = runRef.current;
    runRef.current = null;
    voiceRef.current.stop();
    // Also stop a pending gesture unlock, which is not owned by useVoice yet.
    audioRef.current?.pause();
    run?.focus.release();
    if (mounted.current) setBusy(false);
  }, []);
  const isCurrent = useCallback((run: SpeechRun) => mounted.current && runRef.current === run
    && run.key === latest.current.identity && !latest.current.paused
    && document.visibilityState !== "hidden" && run.focus.isCurrent()
    && scope.current.isCurrent(run.auth), []);

  useEffect(() => {
    mounted.current = true;
    const auth = scope.current;
    const update = (session: unknown) => {
      if (!mounted.current) return;
      if (auth.observeSession(session).changed) { cancel(); setMessage(null); }
    };
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => update(session));
    const revision = auth.revision();
    void supabase.auth.getSession().then(result => {
      if (mounted.current && auth.revision() === revision) update(result.error ? null : result.data.session);
    }).catch(() => {
      if (mounted.current && auth.revision() === revision) update(null);
    });
    const stopWhenHidden = () => { if (document.visibilityState === "hidden") cancel(); };
    document.addEventListener("visibilitychange", stopWhenHidden);
    window.addEventListener("pagehide", cancel);
    // Audio presence is synchronous; React rendering is not the admission gate.
    const unsubscribeFocus = appAudioFocus.subscribe(() => { if (appAudioFocus.blocking()) cancel(); });
    return () => {
      mounted.current = false;
      cancel(); auth.invalidate(); subscription.unsubscribe(); unsubscribeFocus();
      document.removeEventListener("visibilitychange", stopWhenHidden);
      window.removeEventListener("pagehide", cancel);
    };
  }, [cancel]);

  useLayoutEffect(() => { cancel(); setMessage(null); }, [options.identity, options.paused, cancel]);

  const listen = useCallback(async () => {
    if (runRef.current) { cancel(); return; }
    const current = latest.current;
    const element = audioRef.current;
    if (current.paused || !current.text.trim() || !element || document.visibilityState === "hidden") return;
    setMessage(null);
    const auth = scope.current.capture();
    if (!auth) { setMessage("signInToListen"); return; }
    const focus = appAudioFocus.tryAcquireCoach(cancel);
    if (!focus) return;
    const run: SpeechRun = { key: current.identity, auth, focus };
    runRef.current = run;
    setBusy(true);
    const assertCurrent = () => { if (!isCurrent(run)) throw new Error("coach_cancelled"); };
    const finish = (failure: SpeechMessage = null) => {
      if (runRef.current !== run) return;
      runRef.current = null;
      focus.release();
      if (mounted.current) { setBusy(false); setMessage(failure); }
    };
    try {
      // Called directly from the button gesture, before any network/auth await.
      element.src = SILENT_MP3; element.load();
      await element.play();
      assertCurrent();
      element.pause();
      await voiceRef.current.speak(current.text, {
        language: current.language,
        coachingAudioFocus: focus,
        ownerGuard: { accessToken: auth.accessToken, assertCurrent },
        observer: event => {
          if (event.stage === "ended") finish();
          if (event.stage === "error") finish(event.code === "tts_quota" ? "audioQuota" : "audioError");
        },
      });
    } catch {
      // Cancellation is expected; only a still-owned run reports a retry state.
      if (isCurrent(run)) {
        console.warn("[fitness-coach] requested audio unavailable");
        finish("audioError");
      }
    }
  }, [cancel, isCurrent]);
  return { attachAudio, busy, loading: voice.isLoading, message, blocked: blocker !== null, listen, stop: cancel };
}
